import { Plugin } from "vuex";
import {
  DEQUEUED,
  DequeuedMutation,
  DequeueFailedMutation,
  DEQUEUE_FAILED,
  ENQUEUED,
  EnqueuedMutation,
  EnqueueFailedMutation,
  ENQUEUE_FAILED,
  ModuleState as QueueModuleState
} from "./queue";
import {
  DisposeMutation,
  HideMutation,
  ModuleState as ToastsModuleState,
  ShowMutation
} from "./toasts";

type QueueState<Q extends string> = { [P in Q]: QueueModuleState };
type ToastsState<T extends string> = { [P in T]: ToastsModuleState };

export default function createConnector<Q extends string, T extends string>(
  queue: Q,
  toasts: Q
): Plugin<QueueState<Q> & ToastsState<T>> {
  return store => {
    type Operation =
      | typeof DEQUEUED
      | typeof DEQUEUE_FAILED
      | typeof ENQUEUED
      | typeof ENQUEUE_FAILED;
    function createToastId(operation: Operation, id: string) {
      return `${queue}/${operation}/${new Date().valueOf()}/${id}`;
    }

    function handleEnqueueFailure(id: string) {
      const show: ShowMutation = {
        id: createToastId(ENQUEUE_FAILED, id),
        content: {
          style: "danger",
          title: "Save Failed",
          body: "Failed to save article to Read Queue",
          date: new Date()
        }
      };

      store.commit(`${toasts}/show`, show);
    }

    function handleEnqueueSuccess({ entry: { id }, loaded }: EnqueuedMutation) {
      // don't show notification for on-start loading of entries from storage
      if (loaded) {
        return;
      }

      const show: ShowMutation = {
        id: createToastId(ENQUEUED, id),
        content: {
          style: "success",
          title: "Saved",
          body: "Saved article to Read Queue",
          date: new Date()
        }
      };

      store.commit(`${toasts}/show`, show);
    }

    function handleDequeueFailure(id: string) {
      const show: ShowMutation = {
        id: createToastId(DEQUEUE_FAILED, id),
        content: {
          style: "danger",
          title: "Remove Failed",
          body: "Failed to remove article from Read Queue",
          date: new Date()
        }
      };

      store.commit(`${toasts}/show`, show);
    }

    function handleDequeueSuccess(id: string) {
      const show: ShowMutation = {
        id: createToastId(DEQUEUED, id),
        content: {
          style: "success",
          title: "Removed",
          body: "Removed article from Read Queue",
          date: new Date()
        }
      };

      store.commit(`${toasts}/show`, show);
    }

    function handleToastHide(toastId: string) {
      if (toastId.startsWith(queue)) {
        const dispose: DisposeMutation = { id: toastId };
        store.commit(`${toasts}/dispose`, dispose);
      }
    }

    store.subscribe(({ type, payload }) => {
      switch (type) {
        case `${queue}/${ENQUEUE_FAILED}`:
          handleEnqueueFailure((payload as EnqueueFailedMutation).id);
          break;
        case `${queue}/${ENQUEUED}`:
          handleEnqueueSuccess(payload);
          break;
        case `${queue}/${DEQUEUE_FAILED}`:
          handleDequeueFailure((payload as DequeueFailedMutation).id);
          break;
        case `${queue}/${DEQUEUED}`:
          handleDequeueSuccess((payload as DequeuedMutation).id);
          break;
        case `${toasts}/hide`:
          handleToastHide((payload as HideMutation).id);
          break;
      }
    });
  };
}
